// =============================================================================
// campaign-analysis-client.http.ts — Adaptateur HTTP RÉEL du CampaignAnalysisClient (S17 / D2).
// =============================================================================
// Câble le read-model ADMIN de la `CampaignAnalysis` derrière le MÊME DI seam
// (CampaignAnalysisClientProvider) : l'écran consomme l'interface, l'impl est substituée sans
// toucher l'UI.
//
// AUTORITÉ = API/worker (NestJS, AdminSessionGuard, calcul amont @jeko/analytics-engine). Cet
// adaptateur ne porte AUCUNE règle métier : il sérialise les filtres (F), parse la réponse, mappe
// l'erreur. Les 2 routes sont des GET (admin LIT, jamais d'écriture — invariant n°1).
//
// SÉCURITÉ : la session admin est portée par le COOKIE httpOnly posé par l'API (credentials:
// 'include'). L'UI ne lit ni n'écrit le secret de session. Les routes étant en GET, AUCUN en-tête
// CSRF (admin_csrf) n'est requis (le double-submit admin ne couvre que les méthodes MUTANTES —
// parité operational-view-client.http.ts). La publication (bascule publication_state) est une
// MUTATION distincte (Parcours D / ST-BENCH-04) HORS périmètre de ce read-model de lecture.

import {
  CampaignAnalysisApiError,
  type AnalysisComparisonResponseDto,
  type AnalysisPercentilesResponseDto,
  type CampaignAnalysisClient,
  type CampaignAnalysisFilters,
  type CampaignAnalysisMetaDto,
  type CampaignStackReferentialDto,
  type CampaignTargetPriceSummaryDto,
  type ScatterPointsResponseDto,
} from '@/api/campaign-analysis-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API admin campaign-analysis. `baseUrl` par défaut vide
 * (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/admin/*`). En
 * test, on injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpCampaignAnalysisClient implements CampaignAnalysisClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /** Sérialise les filtres serveur (F) en query string (omet les valeurs absentes). */
  private query(filters: CampaignAnalysisFilters): string {
    const params = new URLSearchParams();
    if (filters.profileCode !== undefined && filters.profileCode !== '') {
      params.set('profileCode', filters.profileCode);
    }
    if (filters.seniorityLevel !== undefined) {
      params.set('seniorityLevel', filters.seniorityLevel);
    }
    const qs = params.toString();
    return qs === '' ? '' : `?${qs}`;
  }

  /** Exécute un GET admin (cookie de session) et renvoie le JSON typé, ou lève une erreur mappée. */
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      // Session admin = cookie httpOnly : transmis avec credentials: 'include'.
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<CampaignAnalysisApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur l'analyse de campagne.`;
    return new CampaignAnalysisApiError(message, response.status);
  }

  public getPercentiles(
    campaignId: string,
    filters: CampaignAnalysisFilters,
  ): Promise<AnalysisPercentilesResponseDto> {
    return this.get<AnalysisPercentilesResponseDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/percentiles${this.query(filters)}`,
    );
  }

  /** Sérialise `priorCampaignId` (répété) + filtres (F) en query string ; '' si aucun paramètre. */
  private comparisonQuery(
    priorCampaignIds: readonly string[],
    filters: CampaignAnalysisFilters,
  ): string {
    const params = new URLSearchParams();
    for (const id of priorCampaignIds) {
      params.append('priorCampaignId', id);
    }
    if (filters.profileCode !== undefined && filters.profileCode !== '') {
      params.set('profileCode', filters.profileCode);
    }
    if (filters.seniorityLevel !== undefined) {
      params.set('seniorityLevel', filters.seniorityLevel);
    }
    const qs = params.toString();
    return qs === '' ? '' : `?${qs}`;
  }

  public getAnalysisComparison(
    campaignId: string,
    priorCampaignIds: readonly string[],
    filters: CampaignAnalysisFilters,
  ): Promise<AnalysisComparisonResponseDto> {
    return this.get<AnalysisComparisonResponseDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/analysis-comparison${this.comparisonQuery(priorCampaignIds, filters)}`,
    );
  }

  /** V26-ANALYSE-UI — métadonnées de l'analyse (computedAt, DBU §2.6 l.1345). Endpoint DÉJÀ exposé. */
  public getAnalysis(campaignId: string): Promise<CampaignAnalysisMetaDto> {
    return this.get<CampaignAnalysisMetaDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/analysis`,
    );
  }

  /** V26-ANALYSE-UI / DEC-17 — référentiel « stack » (grain famille) du filtre d'analyse. */
  public getStacks(campaignId: string): Promise<CampaignStackReferentialDto> {
    return this.get<CampaignStackReferentialDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/stacks`,
    );
  }

  /** V26-NUAGE — nuage de points (grain trinôme nominatif, family). GET read-only (aucun CSRF). */
  public getPoints(campaignId: string): Promise<ScatterPointsResponseDto> {
    return this.get<ScatterPointsResponseDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/points`,
    );
  }

  /** L9 — tableau de fin de campagne prix cible + médianes C1/C2 (admin seul). GET read-only (aucun CSRF). */
  public getTargetPriceSummary(campaignId: string): Promise<CampaignTargetPriceSummaryDto> {
    return this.get<CampaignTargetPriceSummaryDto>(
      `/admin/positioning/${encodeURIComponent(campaignId)}/target-price-summary`,
    );
  }
}
